/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// React Query
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "app/config/queryClient";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "Screens/SidenavScreen/Sidenav";
import ConfiguratorScreen from "Screens/ConfiguratorScreen/ConfiguratorScreen";

// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React TS routes
import routes from "routes";

// Protected Route Component
import ProtectedRoute from "components/ProtectedRoute/ProtectedRoute";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const isPublicRoute = pathname.startsWith("/public");

  // Cache for the rtl
  useMemo(() => {
    const pluginRtl: any = rtlPlugin;
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [pluginRtl],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Safari over-scrolling 배경색을 theme에서 가져와서 설정
  useEffect(() => {
    // 공개 페이지는 항상 흰색 배경
    const isPublic = pathname.startsWith("/public");

    // theme에서 배경색 직접 가져오기
    const backgroundColor = isPublic
      ? "#F8F9FA"
      : (darkMode
        ? themeDark.palette.background?.default || "#1a2035"
        : theme.palette.background?.default || "#f0f2f5");

    // html과 body의 배경색을 theme의 기본 배경색으로 설정 (!important로 우선순위 높임)
    document.documentElement.style.setProperty("background-color", backgroundColor, "important");
    document.body.style.setProperty("background-color", backgroundColor, "important");

    // Safari over-scrolling을 위한 추가 설정 (모든 가능한 요소에 적용)
    const rootElement = document.documentElement;
    rootElement.style.setProperty("background-color", backgroundColor, "important");

    // #root 요소도 설정
    const rootDiv = document.getElementById("root");
    if (rootDiv) {
      rootDiv.style.setProperty("background-color", backgroundColor, "important");
    }

    // meta theme-color 태그 업데이트 (Safari over-scrolling 배경색에 영향)
    let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.name = "theme-color";
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = backgroundColor;
  }, [darkMode, pathname]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map((route: any) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // 로그인 페이지는 ProtectedRoute로 감싸지 않음
        const isLoginRoute = route.route === "/authentication/sign-in/basic";
        const isPublicRoute = route.type === "public";

        if (isLoginRoute || isPublicRoute) {
          return <Route path={route.route} element={route.component} key={route.key} />;
        }

        // 나머지 모든 라우트는 ProtectedRoute로 보호
        return (
          <Route
            path={route.route}
            element={<ProtectedRoute>{route.component}</ProtectedRoute>}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const isDev = process.env.NODE_ENV === "development";

  return (
    <QueryClientProvider client={queryClient}>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <CssBaseline />
            {layout === "dashboard" && !isPublicRoute && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName="MediInStep"
                  routes={routes()}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <ConfiguratorScreen />
                {configsButton}
              </>
            )}
            {layout === "vr" && <ConfiguratorScreen />}
            <Routes>
              {getRoutes(routes())}
              <Route path="*" element={<Navigate to="/dashboards/dashboard" />} />
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          {layout === "dashboard" && !isPublicRoute && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="MediInStep"
                routes={routes()}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <ConfiguratorScreen />
              {configsButton}
            </>
          )}
          {layout === "vr" && <ConfiguratorScreen />}
          <Routes>
            {getRoutes(routes())}
            <Route path="*" element={<Navigate to="/dashboards/dashboard" />} />
          </Routes>
          {isDev && <ReactQueryDevtools initialIsOpen={false} />}
        </ThemeProvider>
      )}
    </QueryClientProvider>
  );
}
